import { Modal } from '@design-system/elements';
import {
  NameEmailForm,
  NameEmailFormFields,
} from '@design-system/forms/NameEmailForm';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { ObserverType } from './ObserversTab';

export type ObserverModalProps = {
  color: string;
  setShowModal: () => void;
  isOpen: boolean;
  onSuccess: (observers) => void;
  isEditing: boolean;
  observer?: ObserverType;
  setObserver?: (observer: ObserverType | undefined) => void;
};

export const ObserverModal: React.FC<ObserverModalProps> = ({
  color,
  setShowModal,
  isOpen,
  onSuccess,
  isEditing,
  observer,
  setObserver,
}) => {
  const [disabled, setIsDisabled] = useState(true);
  const { t } = useTranslation();

  const reset = () => {
    setShowModal();
    setIsDisabled(true);
    setObserver ? setObserver(undefined) : null;
  };

  const onCancel = () => {
    setIsDisabled(true);
    setObserver ? setObserver(undefined) : null;
  };

  const handleSubmit = (values: NameEmailFormFields) => {
    onSuccess({
      id: new Date().getTime(),
      ...values,
    });
    reset();
  };

  const handleSuccess = () => {
    handleSubmit(observer as ObserverType);
  };

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  const handleFormChange = ({ isDisabled, ...rest }) => {
    if (
      validateEmail(rest.email) &&
      typeof rest.name === 'string' &&
      rest.name.trim().length > 0
    ) {
      setIsDisabled(false);
      setObserver ? setObserver({ ...rest } as ObserverType) : null;
    } else {
      setIsDisabled(true);
      setObserver ? setObserver({ ...rest } as ObserverType) : null;
    }
  };

  return (
    <Modal
      showModal={isOpen}
      setShowModal={setShowModal}
      onSuccess={handleSuccess}
      showCancel
      isDisabled={disabled}
      closeOnClickOutside
      handleCancel={onCancel}
      cancelText={t(translations.COMMON.CANCEL)}
      width="md:w-6/12 xl:w-4/12"
      title={
        isEditing
          ? t(translations.MODALS.EDIT_OBSERVER_TITLE)
          : t(translations.MODALS.ADD_OBSERVER_TITLE)
      }>
      <NameEmailForm
        primaryColor={color}
        hasButton={false}
        onFormChange={handleFormChange}
        defaultValues={{
          name: observer?.name ?? '',
          email: observer?.email ?? '',
        }}
      />
    </Modal>
  );
};
